/**
 * Project drafts API — list/get/delete via React Query, plus imperative
 * create/update used by the autosave loop (a debounced background side-effect,
 * not a user-triggered mutation).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProjectDraftFull,
  ProjectDraftListResponse,
  ProjectDraftSaveInput,
  ProjectDraftUpdateInput,
} from '@vari-crew/schemas';
import { toast } from 'sonner';
import { apiCall } from '@/lib/api';

export const PROJECT_DRAFTS_KEY = ['project-drafts'] as const;

/** The current user's saved drafts (no payload) — powers the "Saved drafts" list. */
export function useProjectDrafts(enabled = true) {
  return useQuery<ProjectDraftListResponse>({
    queryKey: PROJECT_DRAFTS_KEY,
    enabled,
    queryFn: () => apiCall<ProjectDraftListResponse>('/me/drafts'),
    staleTime: 10_000,
  });
}

/** Fetch one draft with its payload — used to resume. */
export function getDraft(id: string): Promise<ProjectDraftFull> {
  return apiCall<ProjectDraftFull>(`/me/drafts/${id}`);
}

export function createDraft(body: ProjectDraftSaveInput): Promise<ProjectDraftFull> {
  return apiCall<ProjectDraftFull>('/me/drafts', { method: 'POST', body });
}

export function updateDraft(
  id: string,
  body: ProjectDraftUpdateInput,
): Promise<ProjectDraftFull> {
  return apiCall<ProjectDraftFull>(`/me/drafts/${id}`, { method: 'PUT', body });
}

export function deleteDraft(id: string): Promise<void> {
  return apiCall<void>(`/me/drafts/${id}`, { method: 'DELETE' });
}

/** Rename a draft inline — the title becomes user-authored (no auto-renaming). */
export function renameDraft(id: string, title: string): Promise<ProjectDraftFull> {
  return apiCall<ProjectDraftFull>(`/me/drafts/${id}`, { method: 'PATCH', body: { title } });
}

/** Discard a draft from the list (Projects page). */
export function useDeleteProjectDraft() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROJECT_DRAFTS_KEY }),
  });
}

/** Inline-rename a draft from the list. */
export function useRenameProjectDraft() {
  const qc = useQueryClient();
  return useMutation<ProjectDraftFull, Error, { id: string; title: string }>({
    mutationFn: ({ id, title }) => renameDraft(id, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROJECT_DRAFTS_KEY }),
    onError: (e) => toast.error(`Couldn't rename draft: ${e.message}`),
  });
}
